import React, { useEffect, useRef, useState } from "react";
import { Link, ResponsiveHeroImage } from "@quintype/components";
import get from "lodash/get";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { FocusedImage } from "quintype-js";

import { clientWidth, isExternalStory, getStoryUrl, removeHtmlTags } from "../../../utils/utils";
import { FallbackImage } from "../FallbackImage";
import { HyperLink } from "../Hyperlink";
// import { SvgIconHandler } from "../../../../components/atoms/SvgIconHandler";
import LiveButton from "../../../../components/atoms/live-button";
import { YTModal } from "../YTModal";
import { FALLBACK_IMAGE } from "../../../../constants";

import "./hero-image.m.css";

export const HeroImage = ({
  story,
  FullBleed,
  aspectRatio,
  defaultWidth,
  widths,
  isHorizontal,
  isHorizontalMobile,
  isHorizontalWithImageLast,
  defaultFallback,
  isStoryPageImage,
  showIcon,
  isRecommended = false,
  isYTPageImage = false,
  potraitStory = false,
  tagPage = false,
  firstTagPage = false,
  fetchPriorityHigh = false,
  isFeaturedImage = false,
  isPortraitImage = false,
  noHyperlink = false,
  imageWidthOverride,
  imageQualityOverride,
}) => {
  const fallbackImageUrl = FALLBACK_IMAGE;
  const [isDesktop, setIsDesktop] = useState(false);
  const [windowPresent, setWindowPresent] = useState(false);
  const [shouldLoadDeferredImage, setShouldLoadDeferredImage] = useState(false);
  const imageRef = useRef(null);

  const cdnHost = useSelector((state) => get(state, ["qt", "config", "cdn-image"]));
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], ""));

  const showImagePlaceholder = useSelector((state) => get(state, ["qt", "config", "showPlaceholder"]));
  const getPlaceholderStyleName = showImagePlaceholder ? "placeholder" : "";

  const fullBleed = FullBleed ? "" : "with-padding";

  let heroImageClass = "hero-image";
  if (isHorizontal) heroImageClass = "right-padding";
  else if (isHorizontalWithImageLast) heroImageClass = "left-padding";

  const heroImageMobClasses = isHorizontalMobile ? "right-padding-mob" : "";

  const getPadding = (aspectRatio) => {
    const [height, width] = aspectRatio;
    if (height > 0 && width > 0) {
      const padding = (width / height) * 100;
      return padding;
    }
    return 0;
  };

  const [mobileAspectRatio, desktopAspectRatio = mobileAspectRatio] = aspectRatio;
  const isMobile = clientWidth("mobile");

  const imageAspectRatio = isMobile ? mobileAspectRatio : desktopAspectRatio;
  const imagePadding = isMobile ? getPadding(mobileAspectRatio) : getPadding(desktopAspectRatio);
  const slug =
    get(story, ["hero-image-s3-key"]) ||
    get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"]);
  const hyperLink = get(story, ["hero-image-hyperlink"], "");
  const imageCaption = get(story, ["hero-image-caption"], "");
  const altText = get(story, ["hero-image-alt-text"], "");
  const imageAltText = altText
    ? removeHtmlTags(altText)
    : imageCaption
    ? removeHtmlTags(imageCaption)
    : get(story, ["headline"], "");

  const getIcon = (story) => {
    const svgDimensions = isDesktop ? "24" : "20";
    switch (story?.["story-template"]) {
      case "video":
        return (
          <div styleName="video-icon">
            <i />
            {/* <SvgIconHandler
              type="story-play"
              width={svgDimensions}
              height={svgDimensions}
              viewBox={`0 0 ${svgDimensions} ${svgDimensions}`}
            /> */}
          </div>
        );

      case "photo":
        return (
          <div styleName="photo-icon">
            <i/>
            {/* <SvgIconHandler
              type="image"
              width={svgDimensions}
              height={svgDimensions}
              viewBox={`0 0 ${svgDimensions} ${svgDimensions}`}
            /> */}
          </div>
        );
      case "visual-story":
        return (
          <div styleName="story-type-tag">
            <i />
            {/* <SvgIconHandler type="visual-story" width={24} height={24} viewBox="0 0 24 24" /> */}
          </div>
        );
      case "live-blog":
        return <LiveButton story={story} isRecommended={isRecommended} />;
      default:
        break;
    }
  };

  useEffect(() => {
    if (window.innerWidth >= 1200) {
      setIsDesktop(true);
    }
    if (typeof window !== "undefined") {
      setWindowPresent(true);
    }
  }, []);

  let sizes;

  switch (true) {
    case tagPage && firstTagPage:
      sizes = "(max-width: 1160px) 100vw, 360px";
      break;
    case potraitStory:
      sizes = "(max-width: 1160px) 50vw, 267px";
      break;
    case tagPage:
      sizes = "(max-width: 1160px) 33vw, 360px";
      break;
  }

  const imageMeta = get(story, ["hero-image-metadata"], {});
  let rectParam = "";
  const HeroimageWidth =
    imageWidthOverride || (isPortraitImage ? 480 : isFeaturedImage || isStoryPageImage ? 640 : 360);
  const HeroimageHeight = isPortraitImage
    ? "auto"
    : Math.round((HeroimageWidth * imageAspectRatio[1]) / imageAspectRatio[0]);
  const heroImageQuality = imageQualityOverride || 80;
  const imageLoading = isStoryPageImage || isFeaturedImage ? "eager" : "lazy";
  const fetchPriorityImg = isStoryPageImage || isFeaturedImage ? "high" : "auto";
  const shouldDeferHomeImage = pageType === "home-page" && !isStoryPageImage && !isFeaturedImage && !firstTagPage;
  const shouldRenderImage = !shouldDeferHomeImage || shouldLoadDeferredImage;
  const imageAltTitle = (story["hero-image-alt-text"] || story?.headline || "").slice(0, 30);
  if (imageMeta) {
    const focusedImage = new FocusedImage(slug, imageMeta);
    const fullPath = focusedImage?.path([16, 9], {});
    const queryParams = fullPath?.split("?")[1];

    if (queryParams) {
      const params = new URLSearchParams(queryParams);
      const rect = params?.get("rect");
      if (rect) {
        rectParam = `rect=${rect}&`;
      }
    }
  }

  useEffect(() => {
    if (!shouldDeferHomeImage) return;

    if (typeof window === "undefined" || !imageRef.current) return;

    if (!("IntersectionObserver" in window)) {
      setShouldLoadDeferredImage(true);
      return;
    }

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadDeferredImage(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" }
    );

    observer.observe(imageRef.current);

    return () => observer.disconnect();
  }, [shouldDeferHomeImage, slug]);

  const fallbackImage = () => {
    if (!slug) {
      return (
        <>
          {defaultFallback && fallbackImageUrl ? (
            <div
              styleName={`image ${getPlaceholderStyleName}`}
              className="arr--fallback-hero-image"
              data-test-id="arr--fallback-hero-image"
              style={{
                paddingTop: imagePadding + `%`,
              }}
            >
              {showIcon && getIcon(story)}
              <FallbackImage fetchPriorityHigh={fetchPriorityHigh || firstTagPage} />
            </div>
          ) : null}
        </>
      );
    }

    return (
      <>
        <figure
          ref={imageRef}
          styleName={`image ${getPlaceholderStyleName}`}
          className="arr--responsive-hero-image"
          style={{ paddingTop: imagePadding + `%` }}
        >
          {showIcon && getIcon(story)}
          {!shouldRenderImage ? null : !windowPresent && !sizes ? (
            <img
                src={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=${HeroimageWidth}&auto=format,compress&q=${heroImageQuality}`}
                alt={imageAltTitle} title={imageAltTitle} loading={imageLoading}
                style={{ width: "100%" }} width={HeroimageWidth} height={HeroimageHeight} fetchPriority={fetchPriorityImg}
              />
          ) : !windowPresent && sizes ? (
            <picture>
              {potraitStory && (
                <>
                  <source
                    media="(max-width: 480px)"
                    srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=480&auto=format,compress&fit=max&q=80`}
                  />
                  <source
                    media="(min-width: 481px)"
                    srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=640&auto=format,compress&fit=max&q=80`}
                  />
                </>
              )}
              {tagPage && !firstTagPage && (
                <>
                  <source
                    media="(max-width: 379px)"
                    srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=250&auto=format,compress&fit=max&q=80`}
                  />
                  <source
                    media="(max-width: 726px)"
                    srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=480&auto=format,compress&fit=max&q=80`}
                  />
                  <source
                    media="(min-width: 727px)"
                    srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=640&auto=format,compress&fit=max&q=80`}
                  />
                </>
              )}
              {tagPage && firstTagPage && (
                <>
                  <source
                    srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=640&auto=format,compress&fit=max&q=80`}
                  />
                </>
              )}
              <img
                src={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=640&auto=format,compress&fit=max&q=80`}
                alt={imageAltTitle} title={imageAltTitle}
                style={{ width: "100%" }}
              />
            </picture>
          ) : (
              <img
                src={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=${HeroimageWidth}&auto=format,compress&q=${heroImageQuality}`}
                alt={imageAltTitle} title={imageAltTitle} loading={imageLoading}
                style={{ width: "100%" }} width={HeroimageWidth} height={HeroimageHeight} fetchPriority={fetchPriorityImg}
              />
          )}
        </figure>
        {isStoryPageImage && hyperLink && <HyperLink hyperLink={hyperLink} />}
      </>
    );
  };

  if (isYTPageImage) {
    return <YTModal story={story} aspectRatio={[[9, 16]]}></YTModal>;
  }

  return (
    story &&
    (isStoryPageImage || noHyperlink ? (
      <div
        className="arr--hero-image"
        data-test-id="arr--hero-image"
        styleName={`${heroImageClass} ${fullBleed} ${heroImageMobClasses}`}
      >
        {fallbackImage()}
      </div>
    ) : (
      <Link
        className="arr--hero-image"
        data-test-id="arr--hero-image"
        href={getStoryUrl(story, `/${story.slug}`)}
        externalLink={isExternalStory(story)}
        styleName={`${heroImageClass} ${fullBleed} ${heroImageMobClasses}`}
        aria-label="fallback-hero-image"
        disableAjaxLinks={true}
      >
        {fallbackImage()}
      </Link>
    ))
  );
};

HeroImage.propTypes = {
  /** The Story Object from the API response */
  story: PropTypes.object.isRequired,
  /** Removes the Hero Image padding for the image to bleed over the margin */
  FullBleed: PropTypes.bool,
  /** The default width for the image, in case the browser does not support srcset */
  defaultWidth: PropTypes.number,
  /** The list of available widths */
  widths: PropTypes.array,
  // fatch the correct hero image
  aspectRatio: PropTypes.array,

  //  Hints for the browser to choose the best size
  /** This prop is responsible for adding padding-right of the image and remove padding bottom of the image  */
  isHorizontal: PropTypes.bool,
  /** This prop is responsible for adding padding-bottom in desktop and padding-right in mobile */
  isHorizontalMobile: PropTypes.bool,
  // this prop is responsive for rendering fallback image on story page
  defaultFallback: PropTypes.bool,
  isHorizontalWithImageLast: PropTypes.bool,
  isStoryPageImage: PropTypes.bool,
  showIcon: PropTypes.bool,
  tagPage: PropTypes.bool,
  fetchPriorityHigh: PropTypes.bool,
  firstTagPage: PropTypes.bool,
  potraitStory: PropTypes.bool,
  imageWidthOverride: PropTypes.number,
  imageQualityOverride: PropTypes.number,
};

HeroImage.defaultProps = {
  FullBleed: true,
  aspectRatio: [
    [1, 1],
    [16, 9],
  ],
  defaultWidth: 480,
  widths: [250, 480, 640],
  isHorizontal: false,
  isHorizontalMobile: false,
  defaultFallback: true,
  isStoryPageImage: false,
  isHorizontalWithImageLast: false,
  showIcon: true,
};
